import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Swords, TrendingUp, Activity } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LiveFeed({ refreshInterval = 15000 }) {
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActivity();
    const interval = setInterval(loadActivity, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  const loadActivity = async () => {
    try {
      const response = await axios.get(`${API}/activity/live`);
      setActivity(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load activity:', error);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  if (loading) {
    return (
      <Card className="glass-card border-white/10">
        <CardContent className="p-6">
          <div className="text-center">
            <Activity className="animate-spin mx-auto text-purple-500" size={32} />
            <p className="text-slate-400 mt-2">Loading live activity...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="live-feed-widget" className="glass-card border-white/10">
      <CardHeader>
        <CardTitle className="font-rajdhani text-xl text-white flex items-center gap-2">
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [1, 0.5, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-2 h-2 bg-red-500 rounded-full"
          />
          Live Arena Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-purple-900/20 rounded-lg p-3 border border-purple-500/30">
              <div className="text-xs text-slate-400 mb-1">Active Agents</div>
              <div className="text-2xl font-bold text-white">{activity?.total_agents || 0}</div>
            </div>
            <div className="bg-cyan-900/20 rounded-lg p-3 border border-cyan-500/30">
              <div className="text-xs text-slate-400 mb-1">Battles Today</div>
              <div className="text-2xl font-bold text-white">{activity?.total_battles || 0}</div>
            </div>
          </div>

          {/* Recent Battles */}
          <div>
            <div className="text-sm font-rajdhani text-slate-400 mb-2 flex items-center gap-2">
              <Swords size={14} className="text-yellow-400" />
              Latest Battles
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
              <AnimatePresence>
                {activity?.recent_battles.slice(0, 5).map((battle, index) => (
                  <motion.div
                    key={battle.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white/5 rounded-lg p-2 text-sm"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-cyan-400 font-semibold truncate">
                        {battle.challenger_name}
                      </span>
                      <span className="text-slate-600 mx-1">vs</span>
                      <span className="text-purple-400 font-semibold truncate">
                        {battle.opponent_name}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {formatTimeAgo(battle.created_at)}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          {/* Ecosystem Status */}
          <div className="bg-gradient-to-r from-green-900/20 to-transparent p-3 rounded-lg border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <span className="text-sm font-rajdhani text-slate-300">Ecosystem</span>
              <motion.span
                animate={{ opacity: [1, 0.5, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="text-sm font-bold text-green-400"
              >
                LIVE
              </motion.span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
